/**
 * @typedef {[Number, Number]} Point
 */

import { vec2, mat2d } from './matrix';

const tmp1 = vec2.create();
const tmp2 = vec2.create();
const tmp3 = vec2.create();
const transMat = mat2d.create();

/**
 * Rounds the number with the given precision
 * @example round(1.1234, 2) returns 1.12
 * @export
 * @param {Number} value - value to round
 * @param {Number} precision - number of decimals to include
 * @returns{Number} the rounded value
 */
export function round(value, precision) {
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
}

export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

export function sign(value) {
  return value < 0 ? -1 : 1;
}

/**
 * Rotates point around give origo
 * @param   {vec2}  point   Point to be rotated
 * @param   {vec2}  origo   Porint to rotate around
 * @param   {float} radians Angle to rotate
 * @returns {vec2}  returns the point
 */
export function rotateAround(point, origo, radians) {
  if (!radians) return point;
  var sinR = Math.sin(radians),
    cosR = Math.cos(radians),
    delta = vec2.sub([0, 0], point, origo);
  point[0] = cosR * delta[0] - sinR * delta[1] + origo[0];
  point[1] = sinR * delta[0] + cosR * delta[1] + origo[1];
  return point;
}

/**
 *Calculates the cornes of a rotated rectangle
 * @param {Array} corners -	array object to write to (array can be retrieved from getCornerArray)
 * @param   {Number} x -  rect x
 * @param   {Number} y - rect y
 * @param   {Number} width - rect width
 * @param   {Number} height - rect height
 * @param   {Number} rotation - given in radians
 * @returns {Array}  returns the corners array passed in
 */
export function writeRectCorners(corners, x, y, width, height, rotation = 0, origin = corners[0]) {
  mat2d.fromRotation(transMat, rotation);

  vec2.set(corners[0], x, y);
  vec2.set(corners[1], x + width, y);
  vec2.set(corners[2], x + width, y + height);
  vec2.set(corners[3], x, y + height);

  for (var i = 0; i < 4; i++) {
    const corner = corners[i];
    vec2.sub(corner, corner, origin);
    vec2.transformMat2d(corner, corner, transMat);
    // mat2d.mul(corner, transMat, corners[i]);
    vec2.add(corner, corner, origin);
  }

  return corners;
}

export function getCornerArray() {
  return [[0, 0], [0, 0], [0, 0], [0, 0]];
}

/**
 * Checks if points is inside rectangle with given rotation
 * detailed description at pointInside
 * @param   {vec2}    point  -         Point to check aginst
 * @param   {vec4}    rect -           Rectangle [x,y,width,height]
 * @param   {Number}   rotation  -      Rotation in radian
 * @param   {Point} rotationOrigin - Point the rect is rotated around
 * @returns {Boolean} true if point is inside
 */
export function pointInsideRect(point, rect, rotation, rotationOrigin) {
  // Creating B and D points, using tmp1, and tmp3 (using tmp3 because pointInside uses overwrites tmp2)
  vec2.set(tmp1, rect[0] + rect[2], rect[1]);
  // D (using tmp3 because pointInside uses overwrites tmp2)
  vec2.set(tmp3, rect[0], rect[1] + rect[3]);

  return pointInside(
    point,
    rotateAround([rect[0], rect[1]], rotationOrigin, rotation), // A
    rotateAround(tmp1, rotationOrigin, rotation), // B
    rotateAround(tmp3, rotationOrigin, rotation) // D rotates around upper corner
  );
}

/**
 * Checks if point is inside rectangle
 * Thes test only requires three of the cornes of the rect
 * Using this formula: (0<AP⋅AB<AB⋅AB)∧(0<AP⋅AD<AD⋅AD)  ("⋅" is the dot product)
 * @param   {Point} P Point to check
 * @param   {Point} A - topleft corner of rect
 * @param   {Point} B - topRight corner of rect
 * @param   {Point} D - bottomLeft corner of rect
 * @returns {Boolean} true if P is inisde A,B,C,D rect
 */
export function pointInside(P, A, B, D) {
  // tmp2 = AP
  vec2.sub(tmp2, A, P);
  // tmp1 = AB
  vec2.sub(tmp1, A, B);

  var AP_AB = vec2.dot(tmp2, tmp1);
  if (AP_AB <= 0 || AP_AB > vec2.dot(tmp1, tmp1)) {
    return false;
  }
  // tmp1 == AD
  vec2.sub(tmp1, A, D);
  var AP_AD = vec2.dot(tmp2, tmp1);
  // noinspection RedundantIfStatementJS
  if (AP_AD <= 0 || AP_AD > vec2.dot(tmp1, tmp1)) {
    return false;
  }

  return true;
}

/**
 * Calculates the area of a rectangle with corners p1,p2,p3
 * @param  {point} p1 {x:float,y:float}
 * @param  {point} p2 {x:float,y:float}
 * @param  {point} p3 {x:float,y:float}
 * @returns {Number} the area of the triangel
 */
export function triArea(p1, p2, p3) {
  return Math.abs(0.5 * (p1.x * (p2.y - p3.y) + p2.x * (p3.y - p1.y) + p3.x * (p1.y - p2.y)));
}

/**
 * Calculates the area of a rectangle
 * @param  {Number} width - width of rect
 * @param  {Number} height - height of rect
 * @returns {Number} area of rect
 */
export function rectArea(width, height) {
  return width * height;
}

export function getMinPoint(points, axis) {
  axis = axis === 'x' ? 0 : 1;
  var min = points[0][axis];
  for (var i = 1; i < points.length; i++) {
    if (min > points[i][axis]) {
      min = points[i][axis];
    }
  }

  return min;
}

export function getMaxPoint(points, axis) {
  axis = axis === 'x' ? 0 : 1;
  var max = points[0][axis];
  for (var i = 1; i < points.length; i++) {
    if (max < points[i][axis]) {
      max = points[i][axis];
    }
  }

  return max;
}

/**
 * Finds the center of an object rotatet around the top left corner
 * To save memory a center array has to be passed
 * @param   {[x,y]}  center   Point to write center coordinates to
 * @param   {Number} x        x position of object
 * @param   {Number} y        y position of object
 * @param   {Number} width    widht of object
 * @param   {Number} height   height of object
 * @param   {Number} rotation radians rotation of object
 * @returns {center} returns the center object
 */
export function getCenter(center, x, y, width, height, rotation) {
  var cosa = Math.cos(rotation);
  var sina = Math.sin(rotation);
  width /= 2;
  height /= 2;
  center[0] = x + width * cosa - height * sina;
  center[1] = y + width * sina + height * cosa;

  return center;
}
